import { useEffect, useState, useCallback, useRef, Dispatch, SetStateAction, MutableRefObject } from 'react';

import { useAuth } from '../context/AuthContext';
import { eventService } from '../services/eventService';
import { useAppDispatch } from '../store/hooks';
import { api } from '../store/services/api';
import {
  useLazyGetAgentLogsQuery,
  useLazyGetPlanningStatusQuery,
} from '../store/services/planningRuntimeApi';
import type {
  AgentLog as BackendAgentLog,
  PlanningStatus,
} from '../store/services/planningRuntimeApi';

type AgentStatus = 'idle' | 'running' | 'completed' | 'error';

export interface AgentActivity {
  agentType: string;
  projectId: string;
  sessionId: string | null;
  status: AgentStatus;
  summary: string;
  timestamp: string;
}

interface UseAgentStatusResult {
  activeAgents: Record<string, AgentActivity>;
  isRunning: boolean;
  wasCancelled: boolean;
  cancelledPhase: string | null;
}

function updateAgentsState(
  prev: Record<string, AgentActivity>,
  log: BackendAgentLog,
  sessionId: string | null,
): Record<string, AgentActivity> {
  const next = { ...prev };
  const key = log.agent_type;

  let status: AgentStatus = 'running';

  if (log.action === 'completed') status = 'completed';
  else if (log.action === 'error') status = 'error';
  else if (log.action === 'started') status = 'running';

  const existing = prev[key];

  next[key] = {
    agentType: log.agent_type,
    projectId: log.project_id,
    sessionId,
    status,
    summary: log.summary || '',
    // Preserve the original start timestamp so the progress bar keeps advancing.
    // Only reset it when the agent freshly starts (no prior entry, or explicit 'started').
    timestamp: (existing && log.action !== 'started') ? existing.timestamp : log.created_at,
  };

  return next;
}

function getLogTime(log: BackendAgentLog) {
  let timeString = log.created_at;

  if (!timeString.endsWith('Z') && !timeString.includes('+')) {
    timeString += 'Z';
  }

  return new Date(timeString).getTime();
}

function getLogAge(log: BackendAgentLog) {
  return Date.now() - getLogTime(log);
}

type AppDispatch = ReturnType<typeof useAppDispatch>;
type FetchAgentLogs = (projectId: string) => Promise<BackendAgentLog[]>;
type FetchPlanningStatus = (projectId: string) => Promise<PlanningStatus>;

const AGENT_TAB_ACTIONS: Record<string, (d: AppDispatch, projectId: string) => void> = {
  'System Requirements Agent': (d, projectId) => {
    d(api.util.invalidateTags([{ type: 'Requirements', id: projectId }]));
  },
  'System Architect': (d, projectId) => {
    d(api.util.invalidateTags([
      { type: 'Architecture', id: projectId },
      { type: 'ArtifactVersion', id: `${projectId}:architecture` },
    ]));
  },
  'User Flow Planner': (d, projectId) => {
    d(api.util.invalidateTags([{ type: 'UserFlow', id: projectId }]));
  },
  // eslint-disable-next-line no-undef
  'Design Agent': () => { window.dispatchEvent(new CustomEvent('switch_design_subtab', { detail: 'pages' })); },
  'Project Manager': () => { /* data handled by AI Overview */ },
};

const useNavigationHandler = (dispatch: AppDispatch) => useCallback(
  (log: BackendAgentLog, isOld: boolean) => {
    if (!isOld && log.action === 'completed') {
      AGENT_TAB_ACTIONS[log.agent_type]?.(dispatch, log.project_id);
    }
  },
  [dispatch]
);

const useCompletionHandler = (
  setActiveAgents: Dispatch<SetStateAction<Record<string, AgentActivity>>>
) => useCallback(
  (log: BackendAgentLog, isOld: boolean, sessionId: string | null) => {
    if (isOld) {
      setActiveAgents((prev) => {
        const next = { ...prev };

        if (
          next[log.agent_type]?.projectId === log.project_id
          && next[log.agent_type]?.sessionId === sessionId
        ) {
          delete next[log.agent_type];
        }

        return next;
      });
    } else {
      setActiveAgents((prev) => updateAgentsState(prev, log, sessionId));
      setTimeout(() => {
        setActiveAgents((prev) => {
          const next = { ...prev };

          if (
            next[log.agent_type]?.projectId === log.project_id
            && next[log.agent_type]?.sessionId === sessionId
          ) {
            delete next[log.agent_type];
          }

          return next;
        });
      }, 3000);
    }
  },
  [setActiveAgents]
);

const useLogEntryHandler = (
  handleNavigation: (log: BackendAgentLog, isOld: boolean) => void,
  handleCompletion: (log: BackendAgentLog, isOld: boolean, sessionId: string | null) => void,
  setActiveAgents: Dispatch<SetStateAction<Record<string, AgentActivity>>>
) => useCallback(
  (log: BackendAgentLog, sessionId: string | null) => {
    if (log.action === 'cancelled') { setActiveAgents({});

      return; }

    const isOld = getLogAge(log) > 10000;

    handleNavigation(log, isOld);

    if (log.action === 'completed' || log.action === 'error') handleCompletion(log, isOld, sessionId);
    else setActiveAgents((prev) => updateAgentsState(prev, log, sessionId));
  },
  [handleNavigation, handleCompletion, setActiveAgents]
);

interface AgentLogPayload {
  type: string;
  id: string;
  project_id: string;
  session_id?: string | null;
  agent_type: string;
  action: string;
  summary: string | null;
  created_at: string;
  triggered_by?: string;
}

interface PlanningCompletePayload {
  project_id?: string | null;
  session_id?: string | null;
  triggered_by?: string;
  status?: string;
  cancelled_phase?: string | null;
}

interface AgentStateRestorePayload {
  project_id?: string | null;
  session_id?: string | null;
  status?: string;
  user_id?: string;
}

function isProjectPayload(payload: { project_id?: string | null } | null | undefined, projectId: string) {
  return payload?.project_id === projectId;
}

function isSessionPayload(payload: { session_id?: string | null } | null | undefined, sessionId: string | null) {
  return sessionId === null || payload?.session_id === sessionId;
}

function toBackendLog(payload: AgentLogPayload): BackendAgentLog {
  return {
    id: payload.id,
    project_id: payload.project_id,
    agent_type: payload.agent_type,
    action: payload.action,
    summary: payload.summary,
    created_at: payload.created_at,
  };
}

/** Replay logs in order, skipping stale 'started' events that share an
 *  identical timestamp with a 'completed'/'error' for the same agent
 *  (DB ordering race condition within the same second).
 *  Also skip unmatched historical 'started' logs. If a run is truly active
 *  after refresh, the checkpoint-driven agent_state_restore event is the
 *  source of truth; historical logs alone should not resurrect progress. */
function replayLogsWithDedup(logs: BackendAgentLog[], handler: (log: BackendAgentLog) => void) {
  const doneKeys = new Set(
    logs.filter((l) => l.action === 'completed' || l.action === 'error')
      .map((l) => `${l.agent_type}|${l.created_at}`),
  );

  const hasCompletionAfter = (startLog: BackendAgentLog) => logs.some((log) => (
    log.agent_type === startLog.agent_type
    && (log.action === 'completed' || log.action === 'error')
    && getLogTime(log) >= getLogTime(startLog)
  ));

  for (const log of logs) {
    // Skip started log if same-timestamp completed/error exists (original dedup)
    if (log.action === 'started' && doneKeys.has(`${log.agent_type}|${log.created_at}`)) continue;

    // Skip historical starts with no later completion/error.
    if (log.action === 'started' && !hasCompletionAfter(log)) continue;

    handler(log);
  }
}

/** Fetch and replay historical agent logs (oldest first). */
const fetchInitialLogs = (
  pid: string,
  handler: (log: BackendAgentLog) => void,
  shouldApply: () => boolean,
  fetchAgentLogs: FetchAgentLogs,
) => {
  fetchAgentLogs(pid)
    .then((logs) => {
      if (!shouldApply()) return;
      const orderedLogs = [...logs]
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      replayLogsWithDedup(orderedLogs, (log) => {
        if (shouldApply() && log.project_id === pid) handler(log);
      });
    })
    .catch((err) => console.error('Failed to fetch initial agent logs:', err));
};

/** Handle planning_complete SSE events: clear agents and mark done. */
const handlePlanningComplete = (
  payload: PlanningCompletePayload | null,
  currentUserId: string | undefined,
  setActiveAgents: Dispatch<SetStateAction<Record<string, AgentActivity>>>,
  setIsPlanningRunActive: (active: boolean) => void,
  setWasCancelled: Dispatch<SetStateAction<boolean>>,
  setCancelledPhase: Dispatch<SetStateAction<string | null>>,
  doneRef: MutableRefObject<boolean>,
) => {
  if (payload?.triggered_by && currentUserId && payload.triggered_by !== currentUserId) return;
  if (payload?.status === 'cancelled') {
    setWasCancelled(true);
    setCancelledPhase(payload.cancelled_phase ?? 'auto');
  } else {
    setWasCancelled(false);
    setCancelledPhase(null);
  }
  // paused_for_review is treated as full completion — the tracker disappears.
  // When the user clicks Continue, the backend emits new 'started' agent logs
  // which re-trigger the tracker via the agent_log handler (line ~202).
  doneRef.current = true;
  setIsPlanningRunActive(false);
  setActiveAgents({});
};

const useAgentSSE = (
  projectId: string | null,
  sessionId: string | null,
  currentUserId: string | undefined,
  fetchAgentLogs: FetchAgentLogs,
  fetchPlanningStatus: FetchPlanningStatus,
  handleLogEntry: (log: BackendAgentLog, sessionId: string | null) => void,
  setActiveAgents: Dispatch<SetStateAction<Record<string, AgentActivity>>>,
  setWasCancelled: Dispatch<SetStateAction<boolean>>,
  setCancelledPhase: Dispatch<SetStateAction<string | null>>,
) => {
  const [planningRunState, setPlanningRunState] = useState<{
    projectId: string | null;
    sessionId: string | null;
    active: boolean;
  }>({ projectId: null, sessionId: null, active: false });
  const doneRef = useRef(false);
  const activeProjectRef = useRef<string | null>(null);
  const activeSessionRef = useRef<string | null>(null);

  useEffect(() => {
    const currentSessionId = sessionId ?? null;

    activeProjectRef.current = projectId;
    activeSessionRef.current = currentSessionId;
    doneRef.current = true;
    setPlanningRunState({ projectId, sessionId: currentSessionId, active: false });
    setActiveAgents({});
    setWasCancelled(false);
    setCancelledPhase(null);

    if (!projectId) return undefined;

    const scopedProjectId = projectId;
    const scopedSessionId = currentSessionId;
    const isCurrentProject = () => (
      activeProjectRef.current === scopedProjectId
      && activeSessionRef.current === scopedSessionId
    );
    const setScopedPlanningRunActive = (active: boolean) => {
      if (isCurrentProject()) {
        setPlanningRunState({ projectId: scopedProjectId, sessionId: scopedSessionId, active });
      }
    };

    doneRef.current = false;
    eventService.connect(scopedProjectId);
    fetchInitialLogs(
      scopedProjectId,
      (log) => handleLogEntry(log, scopedSessionId),
      () => isCurrentProject() && !doneRef.current,
      fetchAgentLogs,
    );
    const reconcilePlanningStatus = () => {
      if (!isCurrentProject() || doneRef.current) return;
      fetchPlanningStatus(scopedProjectId).then((status) => {
        if (!isCurrentProject()) return;
        if (status.is_active) {
          doneRef.current = false;
          setScopedPlanningRunActive(true);
          setWasCancelled(false);
          setCancelledPhase(null);
          return;
        }

        // Durable run state is the source of truth if the terminal SSE event
        // is missed during a reconnect or filtered out by session metadata.
        doneRef.current = true;
        setScopedPlanningRunActive(false);
        setActiveAgents({});
        if (status.cancelled_phase) {
          setWasCancelled(true);
          setCancelledPhase(status.cancelled_phase);
        } else {
          setWasCancelled(false);
          setCancelledPhase(null);
        }
      }).catch(() => {});
    };

    reconcilePlanningStatus();
    const unsubs = [
      eventService.subscribe('agent_log', (data: unknown) => {
        const payload = data as AgentLogPayload;

        if (payload?.type === 'agent_log') {
          if (
            !isCurrentProject()
            || !isProjectPayload(payload, scopedProjectId)
            || !isSessionPayload(payload, scopedSessionId)
          ) return;
          if (payload.triggered_by && currentUserId && payload.triggered_by !== currentUserId) return;
          if (payload.action === 'started') {
            doneRef.current = false;
            setScopedPlanningRunActive(true);
            setWasCancelled(false);
            setCancelledPhase(null);
          }
          if (!doneRef.current) handleLogEntry(toBackendLog(payload), scopedSessionId);
        }
      }),
      eventService.subscribe('agent_state_restore', (data: unknown) => {
        const payload = data as AgentStateRestorePayload;

        if (
          !isCurrentProject()
          || !isProjectPayload(payload, scopedProjectId)
          || !isSessionPayload(payload, scopedSessionId)
        ) return;
        if (payload?.user_id && currentUserId && payload.user_id !== currentUserId) return;
        if (payload?.status === 'running') {
          setScopedPlanningRunActive(true);
          setWasCancelled(false);
          setCancelledPhase(null);
          // Seed activeAgents so busy states recover immediately after refresh.
          // Without this, replayed logs are all > 10s old and get removed.
          setActiveAgents((prev) => {
            if (
              Object.values(prev).some((agent) => (
                agent.projectId === scopedProjectId && agent.sessionId === scopedSessionId
              ))
            ) return prev;

            return {
              ...prev,
              'Tech Lead Agent': {
                agentType: 'Tech Lead Agent',
                projectId: scopedProjectId,
                sessionId: scopedSessionId,
                status: 'running',
                summary: 'Resuming...',
                timestamp: new Date().toISOString(),
              },
            };
          });
        }
      }),
      eventService.subscribe('planning_complete', (data: unknown) => {
        const payload = data as PlanningCompletePayload;

        if (
          !isCurrentProject()
          || !isProjectPayload(payload, scopedProjectId)
          || !isSessionPayload(payload, scopedSessionId)
        ) return;
        handlePlanningComplete(
          payload,
          currentUserId, setActiveAgents, setScopedPlanningRunActive,
          setWasCancelled, setCancelledPhase, doneRef,
        );
      }),
    ];

    return () => {
      unsubs.forEach((fn) => fn());
      if (isCurrentProject()) {
        doneRef.current = true;
      }
    };
  }, [
    projectId, sessionId, currentUserId, fetchAgentLogs, fetchPlanningStatus,
    handleLogEntry, setActiveAgents, setWasCancelled, setCancelledPhase,
  ]);

  return (
    planningRunState.projectId === projectId
    && planningRunState.sessionId === (sessionId ?? null)
    && planningRunState.active
  );
};

export const useAgentStatus = (projectId: string | null, sessionId: string | null = null): UseAgentStatusResult => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const [triggerAgentLogs] = useLazyGetAgentLogsQuery();
  const [triggerPlanningStatus] = useLazyGetPlanningStatusQuery();
  const fetchAgentLogs = useCallback(
    (pid: string) => triggerAgentLogs(pid, true).unwrap(),
    [triggerAgentLogs],
  );
  const fetchPlanningStatus = useCallback(
    (pid: string) => triggerPlanningStatus(pid, true).unwrap(),
    [triggerPlanningStatus],
  );
  const [activeAgents, setActiveAgents] = useState<Record<string, AgentActivity>>({});
  const [wasCancelled, setWasCancelled] = useState(false);
  const [cancelledPhase, setCancelledPhase] = useState<string | null>(null);
  const handleNavigation = useNavigationHandler(dispatch);
  const handleCompletion = useCompletionHandler(setActiveAgents);
  const handleLogEntry = useLogEntryHandler(handleNavigation, handleCompletion, setActiveAgents);
  const isSseRunning = useAgentSSE(
    projectId,
    sessionId,
    user?.id,
    fetchAgentLogs,
    fetchPlanningStatus,
    handleLogEntry,
    setActiveAgents,
    setWasCancelled,
    setCancelledPhase,
  );
  const scopedSessionId = sessionId ?? null;
  const scopedActiveAgents = Object.fromEntries(
    Object.entries(activeAgents).filter(([, agent]) => (
      agent.projectId === projectId && agent.sessionId === scopedSessionId
    )),
  );
  const isRunning = isSseRunning || Object.values(scopedActiveAgents).some((a) => a.status === 'running');

  useEffect(() => {
    if (!projectId) {
      setWasCancelled(false);
      setCancelledPhase(null);
      return undefined;
    }

    setWasCancelled(false);
    setCancelledPhase(null);

    let cancelled = false;
    fetchPlanningStatus(projectId).then((status) => {
      if (cancelled) return;
      if (status.is_active) {
        setWasCancelled(false);
        setCancelledPhase(null);
      } else if (status.cancelled_phase) {
        setWasCancelled(true);
        setCancelledPhase(status.cancelled_phase);
      }
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [fetchPlanningStatus, projectId, sessionId]);

  return {
    activeAgents: scopedActiveAgents,
    isRunning,
    wasCancelled,
    cancelledPhase,
  };
};
